import type { DirectoryData, FSNode, Result } from '../types';

export const REQUIRED_PROJECT_FILES: readonly string[] = ['project.xml', 'globals.xml'];

const getDirectoryChildren = (directoryData: DirectoryData): FSNode[] => {
    const files: FSNode[] = directoryData.files ?? [];
    if (files.length === 0) return [];

    const hasProjectFileAtTopLevel: boolean = files.some(
        (node) => node.type === 'file' && REQUIRED_PROJECT_FILES.includes(node.name)
    );
    if (hasProjectFileAtTopLevel) return files;

    const root: FSNode | undefined = files[0];
    if (files.length === 1 && root?.type === 'directory' && root.children) {
        return root.children;
    }

    return files;
};

export const isValidLVGLProject = (directoryData: DirectoryData): Result => {
    const missingFiles: string[] = REQUIRED_PROJECT_FILES.filter(
        (file) => !getDirectoryChildren(directoryData).some((child) => child.name === file)
    );
    return {
        success: missingFiles.length === 0,
        error:
            missingFiles.length > 0
                ? `This folder is not a valid LVGL UI project. Missing required project files: ${missingFiles.join(', ')}`
                : undefined
    };
};

export type ResolvedLvglProjectDirectory = {
    success: boolean;
    editorPath?: string;
    error?: string;
};

/** Validates that the selected folder is the LVGL UI project root (project.xml and globals.xml at this path). */
export const resolveLvglProjectDirectory = (directoryData: DirectoryData): ResolvedLvglProjectDirectory => {
    const rootChildren: FSNode[] = getDirectoryChildren(directoryData);
    const rootMissingFiles: string[] = REQUIRED_PROJECT_FILES.filter(
        (file) => !(rootChildren ?? []).some((child) => child.name === file)
    );

    if (rootMissingFiles.length === 0) return { success: true, editorPath: directoryData.dirPath };

    console.warn(`Missing required project files: ${rootMissingFiles.join(', ')}`);
    return {
        success: false,
        error: `This folder is not a valid LVGL UI project. Missing required project files: ${rootMissingFiles.join(', ')}`
    };
};

export const base64FromUint8Array = (data: Uint8Array): string => {
    const chunkSize: 32768 = 0x8000;
    let binary: string = '';
    for (let i = 0; i < data.length; i += chunkSize) {
        const chunk: Uint8Array<ArrayBufferLike> = data.subarray(i, i + chunkSize);
        binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
};
